import {Button, Col, Form, Row} from "react-bootstrap";
import {t} from "ttag";
import {ReactNode, useId, useState} from "react";
import {Board, Question, QuestionInputConfig, QuestionStatus} from "../javaTypes";
import {TopicSort} from "./question/TopicEditor";
import {getRuleSort} from "./question/RuleEditor";

enum RenderFormat {
    WEB, TABLE, HTML, CSV, JSON
}
enum QuestionSort {
    UPDATE_DESC = "UPDATE_DESC", UPDATE_ASC = "UPDATE_ASC", NUMBER_DESC = "NUMBER_DESC", NUMBER_ASC = "NUMBER_ASC", TOPIC = "TOPIC", RULE = "RULE"
}

export default function AdvancedView({board, questions, renderQuestion} : {board: Board, questions: Question[], renderQuestion: (q: Question) => JSX.Element}) {
    const fromId = useId();
    const toId = useId();
    const sortId = useId();
    const fontId = useId();

    const [format, setFormat] = useState(RenderFormat.WEB);
    const [fontSize, setFontSize] = useState(16);
    const [sort, setSort] = useState(QuestionSort.UPDATE_DESC);
    // we could default from to 1 week back. I don't really like it tho. new Date(Date.now() - 7 * 24 * 60 * 60000).toISOString().split('T')[0]); // default to 1 week back
    const [from, setFrom] = useState<string>();
    const [to, setTo] = useState<string>();

    const showTopic = board.config.enableTopic !== QuestionInputConfig.DISABLED;
    const showRule = board.config.enableRule !== QuestionInputConfig.DISABLED;

    const questionSort = (a: Question, b: Question) => {
        switch (sort) {
            case QuestionSort.NUMBER_ASC: return a.number - b.number;
            case QuestionSort.NUMBER_DESC: return b.number - a.number;
            case QuestionSort.TOPIC: return TopicSort(a.topic, b.topic);
            case QuestionSort.RULE: return getRuleSort(board.season.program).call(null, a.rule, b.rule);
            case QuestionSort.UPDATE_ASC: return a.answer.modifiedAt - b.answer.modifiedAt;
            case QuestionSort.UPDATE_DESC:
            default: return b.answer.modifiedAt - a.answer.modifiedAt;
        }
    }

    const questionFilter = (q: Question) => {
        if (q.status !== QuestionStatus.ANSWERED) return false;
        if (from && q.answer.modifiedAt < Date.parse(from)) return false;
        if (to && q.answer.modifiedAt > (Date.parse(to) + 60000 * 60 * 24)) return false;
        return true;
    }

    const questionRender = (q: Question) => {
        const dt = (d: string | number) => new Date(d).toISOString();
        const csvEncode = (s: string) => "\"" + s.replaceAll("\"", "\"\"") + "\"";
        switch (format) {
            case RenderFormat.WEB: return renderQuestion(q);
            case RenderFormat.JSON:
                const cpy : any = Object.assign({}, q, {answer: Object.assign({}, q.answer)});
                for (const k of ["questionId", "boardId", "status", "asker", "createdBy", "modifiedBy", "submittedAt"]) {
                    delete cpy[k];
                    delete cpy.answer[k];
                }
                delete cpy.createdAt
                delete cpy.modifiedAt
                cpy.answer.createdAt = dt(cpy.answer.createdAt);
                cpy.answer.modifiedAt = dt(cpy.answer.modifiedAt);
                return cpy as Partial<Question>;
            case RenderFormat.CSV:
                // {q.topic}, {q.rule}
                return <div key={q.number}>{q.number}, {csvEncode(q.title)}, {csvEncode(q.content)}, {csvEncode(q.answer.content)}, {dt(q.answer.createdAt)}, {dt(q.answer.modifiedAt)}</div>
            case RenderFormat.TABLE:
                return <tr key={q.number}>
                    <td>{q.number}</td>
                    {showTopic ? <td>{q.topic}</td> : null}
                    {showRule ? <td>{q.rule}</td> : null}
                    <td dangerouslySetInnerHTML={{__html: q.content}}></td>
                    <td dangerouslySetInnerHTML={{__html: q.answer.content}}></td>
                </tr>
            case RenderFormat.HTML:
                return <div key={q.number} className={"basicQuestion"}>
                    <h5>{board.slug}{q.number}: {q.title}</h5>
                    <div dangerouslySetInnerHTML={{__html: q.content}}></div>
                    <div><strong>Answer:</strong></div>
                    <div dangerouslySetInnerHTML={{__html: q.answer.content}}></div>
                    <small style={{color: "#777777"}}>Updated {new Date(q.answer.modifiedAt).toLocaleString()}</small>
                    <hr/>
                </div>
        }
    }

    const formattedContent = questions.filter(questionFilter).sort(questionSort).map(questionRender);
    let renderedContent;
    switch (format) {
        case RenderFormat.JSON: renderedContent = <pre>{JSON.stringify(formattedContent, undefined, 4)}</pre>; break;
        case RenderFormat.TABLE: renderedContent = (
                <table id={"questionTable"}>
                    <thead>
                        <th>Number</th>
                        {showTopic ? <th>Topic</th> : null}
                        {showRule ? <th>Rule</th> : null}
                        <th>Question</th>
                        <th>Answer</th>
                    </thead>
                    <tbody>{formattedContent as ReactNode[]}</tbody>
                </table>
            );
            break;
        default: renderedContent = formattedContent as JSX.Element[];
    }

    return <div>
        <div id={"advancedTop"}>
            <Form>
                <Row id={"viewControls"} className={"align-items-center"}>
                    <Col xs={"auto"}>
                        <Form.Group id={sortId}>
                            <Form.Label>Render Format</Form.Label>
                            <Form.Select value={format} onChange={(e) => setFormat(parseInt(e.target.value))}>
                                <option value={RenderFormat.WEB}>Full Web</option>
                                <option value={RenderFormat.TABLE}>Table</option>
                                <option value={RenderFormat.HTML}>Basic HTML</option>
                                {/* TODO backend handing the raw */}
                                {/*<option value={RenderFormat.CSV}>CSV</option>*/}
                                {/*<option value={RenderFormat.JSON}>JSON</option>*/}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col xs={"auto"}>
                        <Form.Group id={fontId}>
                            <Form.Label>Base Font Size</Form.Label>
                            <Form.Control as={"input"} type={"number"} value={fontSize} style={{width: "13ch"}}
                                          onChange={e =>  setFontSize(parseInt(e.target.value))} />
                        </Form.Group>
                    </Col>
                    <Col xs={"auto"}>
                        <Form.Group id={sortId}>
                            <Form.Label>Sort</Form.Label>
                            <Form.Select onChange={e => setSort(e.target.value as QuestionSort)}>
                                <option value={QuestionSort.UPDATE_DESC}>Updated Descending</option>
                                <option value={QuestionSort.UPDATE_ASC}>Updated Ascending</option>
                                <option value={QuestionSort.NUMBER_DESC}>Number Descending</option>
                                <option value={QuestionSort.NUMBER_ASC}>Number Ascending</option>
                                {showTopic ? <option value={QuestionSort.TOPIC}>Topic / Section</option> : null }
                                {showRule ? <option value={QuestionSort.RULE}>Rule</option> : null }
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col xs={"auto"}>
                        <Form.Group id={fromId}>
                            <Form.Label>From</Form.Label>
                            <Form.Control as={"input"} type={"date"} value={from} onChange={e => setFrom(e.target.value)}/>
                        </Form.Group>
                    </Col>
                    <Col xs={"auto"}>
                        <Form.Group id={toId}>
                            <Form.Label>To</Form.Label>
                            <Form.Control as={"input"} type={"date"} value={to} onChange={e => setTo(e.target.value)}/>
                        </Form.Group>
                    </Col>
                    <Col xs={"auto"}>
                        <Button variant={"secondary"} style={{marginTop: '1.5rem'}} onClick={() => window.print()}><i className={"fa fa-print"}/> </Button>
                    </Col>

                {/*    TODO: multi-select rule, multi-select topic, general text search */}

                </Row>
            </Form>
            {/* TODO when backend support this */}
            {/*<a href={`raw?format=${format}&fontSize=${fontSize}&sort=${sort}&to=${to}&from=${from}`} hidden={![RenderFormat.CSV, RenderFormat.JSON, RenderFormat.HTML].includes(format)}>Open raw content</a>*/}
            <hr/>
        </div>
        <div id={"formattedView"} style={{fontSize: fontSize}}>
            {renderedContent}
        </div>
    </div>
}